import { useCallback, useEffect, useRef, useState } from 'react';
import type { SerializeFrom } from '@remix-run/node';
import { useFetcher } from '@remix-run/react';
import {
    CONVERSATIONS_API,
    type loader,
} from '~/routes/remix-api.chat.conversations';
import type { PartialConversation } from '../types';

type ConversationsResult = NonNullable<SerializeFrom<typeof loader>>;
type PageInfo = ConversationsResult['pageInfo'];

/**
 * Hook to get the user's conversations.
 */
export function useConversations() {
    const isFetched = useRef(false);
    const [conversations, setConversations] = useState<
        PartialConversation[] | null
    >(null);
    const [pageInfo, setPageInfo] = useState<PageInfo | null>(null);
    const fetcher = useFetcher<typeof loader>();
    // TODO: pagination, sorting, filtering

    useEffect(() => {
        // Fetch initial conversations on mount.
        if (fetcher.state === 'idle' && !isFetched.current) {
            fetcher.load(CONVERSATIONS_API);
            isFetched.current = true;
        } else if (
            isFetched.current &&
            !fetcher.data &&
            fetcher.state === 'idle'
        ) {
            // If the conversations are still not fetched, try again.
            isFetched.current = false;
        } else if (fetcher.data) {
            setConversations(fetcher.data.conversations);
            setPageInfo(fetcher.data.pageInfo);
        }
    }, [fetcher]);

    return {
        conversations,
        pageInfo,
        refetch: useCallback(() => {
            fetcher.load(CONVERSATIONS_API);
        }, [fetcher]),
    };
}
